import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Instagram,
  Linkedin,
  Youtube,
  FileText,
  Users,
  Clock,
  Calendar,
  Send,
  Shield,
  Lock,
  ExternalLink,
  Scale,
  Mail,
  Phone,
  HelpCircle,
  Briefcase
} from 'lucide-react';

const SnapchatIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M12 3.25c2.45 0 4.25 1.86 4.25 4.45v2.22c0 .38.23.72.58.86.59.23 1.17.39 1.73.48-.29.63-.93 1.07-1.82 1.25.44.82 1.23 1.41 2.34 1.74-.94.38-2.04.32-3.2-.17-.78.86-1.83 1.42-3.02 1.61l-.34 1.15h-1.04l-.34-1.15c-1.19-.19-2.24-.75-3.02-1.61-1.16.49-2.26.55-3.2.17 1.11-.33 1.9-.92 2.34-1.74-.89-.18-1.53-.62-1.82-1.25.56-.09 1.14-.25 1.73-.48.35-.14.58-.48.58-.86V7.7C7.75 5.11 9.55 3.25 12 3.25Z" />
    <path d="M9.25 10.75c.7.43 1.64.68 2.75.68s2.05-.25 2.75-.68" />
  </svg>
);

const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M15.84 2v2.647a3.957 3.957 0 0 0 3.507 3.927v2.665a6.47 6.47 0 0 1-3.507-1.02v5.063a5.845 5.845 0 1 1-5.844-5.843c.27 0 .538.02.803.058v2.73a3.13 3.13 0 0 0-.803-.104 3.16 3.16 0 1 0 3.16 3.16V2h2.684Z" />
  </svg>
);

const XIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117Z" />
  </svg>
);

const CountUpNumber = ({ value, suffix = '' }) => {
  const [displayValue, setDisplayValue] = useState(0);
  const numberRef = useRef(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const element = numberRef.current;

    if (!element) {
      return undefined;
    }

    const animate = () => {
      const duration = 1300;
      const startTime = performance.now();

      const tick = (now) => {
        const progress = Math.min((now - startTime) / duration, 1);
        const easedProgress = 1 - Math.pow(1 - progress, 3);

        setDisplayValue(Math.round(value * easedProgress));

        if (progress < 1) {
          requestAnimationFrame(tick);
        }
      };

      requestAnimationFrame(tick);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          animate();
        }
      },
      { threshold: 0.35 }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [value]);

  return (
    <span ref={numberRef}>
      {displayValue.toLocaleString('en-US')}
      {suffix}
    </span>
  );
};

const FooterPro = () => {
  const { i18n } = useTranslation();
  const currentYear = 2026;
  const copyrightYear = 2026;
  const [email, setEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState('');
  const isArabic = i18n.language === 'ar';
  const copyrightText = isArabic
    ? `\u062c\u0645\u064a\u0639 \u0627\u0644\u062d\u0642\u0648\u0642 \u0645\u062d\u0641\u0648\u0638\u0629 \u0644\u062f\u0649 \u0634\u0631\u0643\u0629 \u0648\u0627\u0632\u0646 \u0644\u0644\u0645\u062d\u0627\u0645\u0627\u0629 \u0648\u0627\u0644\u0627\u0633\u062a\u0634\u0627\u0631\u0627\u062a \u0627\u0644\u0642\u0627\u0646\u0648\u0646\u064a\u0629 \u00a9 ${copyrightYear}`
    : `\u00a9 ${copyrightYear} Wazin Law Firm & Legal Consultancy. All rights reserved.`;

  const socialLinks = [
    { icon: XIcon, href: '#', label: 'X' },
    { icon: Linkedin, href: '#', label: 'LinkedIn' },
    { icon: Instagram, href: '#', label: 'Instagram' },
    { icon: SnapchatIcon, href: '#', label: 'Snapchat' },
    { icon: TikTokIcon, href: '#', label: 'TikTok' },
    { icon: Youtube, href: '#', label: 'YouTube' }
  ];

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email && email.includes('@')) {
      setSubscribeStatus('success');
      setEmail('');
      setTimeout(() => setSubscribeStatus(''), 3000);
    } else {
      setSubscribeStatus('error');
      setTimeout(() => setSubscribeStatus(''), 3000);
    }
  };

  const servicesLinks = [
    { text: i18n.language === 'ar' ? 'الاستشارات القانونية' : 'Legal Consultation', href: '/services', icon: Scale },
    { text: i18n.language === 'ar' ? 'صياغة العقود' : 'Contract Drafting', href: '/services', icon: FileText },
    { text: i18n.language === 'ar' ? 'التمثيل القانوني' : 'Legal Representation', href: '/services', icon: Users },
    { text: i18n.language === 'ar' ? 'الباقات القانونية' : 'Legal Packages', href: '/packages', icon: Briefcase },
    { text: i18n.language === 'ar' ? 'العقود الرقمية' : 'Digital Contracts', href: '/contracts', icon: FileText }
  ];

  const importantLinks = [
    { text: i18n.language === 'ar' ? 'من نحن' : 'About Us', href: '/#about' },
    { text: i18n.language === 'ar' ? 'فريقنا القانوني' : 'Our Legal Team', href: '/#team' },
    { text: i18n.language === 'ar' ? 'تجارب العملاء' : 'Client Testimonials', href: '/#testimonials' },
    { text: i18n.language === 'ar' ? 'سياسة الخصوصية' : 'Privacy Policy', href: '/privacy', external: true },
    { text: i18n.language === 'ar' ? 'الشروط والأحكام' : 'Terms & Conditions', href: '/terms', external: true }
  ];

  const supportLinks = [
    { text: i18n.language === 'ar' ? 'اتصل بنا' : 'Contact Us', href: '/contact', icon: Mail },
    { text: i18n.language === 'ar' ? 'الأسئلة الشائعة' : 'FAQ', href: '/#faq', icon: HelpCircle },
    { text: i18n.language === 'ar' ? 'حجز استشارة' : 'Book Consultation', href: '/book-consultation', icon: Phone },
    { text: i18n.language === 'ar' ? 'تتبع القضايا' : 'Track Cases', href: '/client/dashboard', icon: FileText }
  ];

  const mediaLinks = [
    { text: i18n.language === 'ar' ? 'الأخبار والمقالات' : 'News & Articles', href: '/#news' },
    { text: i18n.language === 'ar' ? 'الفعاليات' : 'Events', href: '/#events' },
    { text: i18n.language === 'ar' ? 'المحتوى القانوني' : 'Legal Content', href: '/blog' }
  ];

  const aboutLinks = [
    { text: i18n.language === 'ar' ? 'رؤيتنا' : 'Our Vision', href: '/#about' },
    { text: i18n.language === 'ar' ? 'رسالتنا' : 'Our Mission', href: '/#about' },
    { text: i18n.language === 'ar' ? 'المقر الرئيسي' : 'Head Office', href: '/#locations' },
    { text: i18n.language === 'ar' ? 'انضم إلينا' : 'Join Us', href: '/careers', external: true }
  ];

  const workingHours = {
    days: i18n.language === 'ar' ? 'الأحد - الخميس' : 'Sunday - Thursday',
    hours: i18n.language === 'ar' ? '8:00 صباحًا - 4:00 عصرًا' : '8:00 AM - 4:00 PM'
  };

  const wazinData = [
    {
      value: 18,
      labelAr: 'طلبات قانونية جديدة',
      labelEn: 'New Legal Requests',
      icon: Briefcase
    },
    {
      value: 12,
      labelAr: 'ملفات منجزة',
      labelEn: 'Completed Files',
      icon: FileText
    },
    {
      value: 27,
      labelAr: 'وكالات وتوثيق',
      labelEn: 'POA & Notarization',
      icon: Shield
    },
    {
      value: 9,
      suffix: '+',
      labelAr: 'ملكية فكرية وعلامات',
      labelEn: 'IP & Trademarks',
      icon: Scale
    }
  ];

  const wazinDataTitleAr = '\u0648\u0627\u0632\u0646 \u0628\u064a\u0627\u0646\u0627\u062a';
  const wazinDataHeadlineAr = '\u0645\u0624\u0634\u0631\u0627\u062a \u062a\u0634\u063a\u064a\u0644\u064a\u0629 \u0645\u062e\u062a\u0635\u0631\u0629';
  const wazinDataDescriptionAr = '\u0627\u0644\u0628\u064a\u0627\u0646\u0627\u062a \u0627\u0644\u0645\u0639\u0631\u0648\u0636\u0629 \u0644\u0622\u062e\u0631 7 \u0623\u064a\u0627\u0645.';
  const wazinDataLabelsAr = [
    '\u0637\u0644\u0628\u0627\u062a \u0642\u0627\u0646\u0648\u0646\u064a\u0629 \u062c\u062f\u064a\u062f\u0629',
    '\u0645\u0644\u0641\u0627\u062a \u0645\u0646\u062c\u0632\u0629',
    '\u0648\u0643\u0627\u0644\u0627\u062a \u0648\u062a\u0648\u062b\u064a\u0642',
    '\u0645\u0644\u0643\u064a\u0629 \u0641\u0643\u0631\u064a\u0629 \u0648\u0639\u0644\u0627\u0645\u0627\u062a'
  ];

  return (
    <footer className="bg-wazin-navy text-white">
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-right">
              <h3 className="text-xl font-bold text-white mb-2">
                {i18n.language === 'ar' ? 'اشترك في النشرة البريدية' : 'Subscribe to Newsletter'}
              </h3>
              <p className="text-gray-300 text-sm">
                {i18n.language === 'ar'
                  ? 'احصل على آخر التحديثات القانونية والأخبار مباشرة في بريدك.'
                  : 'Get the latest legal updates and news directly to your inbox.'}
              </p>
            </div>
            <form onSubmit={handleSubscribe} className="flex gap-2 w-full md:w-auto">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={i18n.language === 'ar' ? 'أدخل بريدك الإلكتروني' : 'Enter your email'}
                className="px-4 py-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-wazin-gold w-full md:w-80"
              />
              <button
                type="submit"
                className="px-6 py-3 bg-wazin-gold hover:bg-wazin-gold-dark text-white font-bold rounded-lg transition-all flex items-center gap-2 whitespace-nowrap"
              >
                {i18n.language === 'ar' ? 'اشترك' : 'Subscribe'}
                <Send className="w-4 h-4" />
              </button>
            </form>
          </div>
          {subscribeStatus === 'success' && (
            <p className="text-green-400 text-sm mt-2 text-center">
              {i18n.language === 'ar' ? 'تم الاشتراك بنجاح!' : 'Successfully subscribed!'}
            </p>
          )}
          {subscribeStatus === 'error' && (
            <p className="text-red-400 text-sm mt-2 text-center">
              {i18n.language === 'ar' ? 'يرجى إدخال بريد إلكتروني صحيح' : 'Please enter a valid email'}
            </p>
          )}
        </div>
      </div>

      <div className="w-full px-8 sm:px-12 lg:px-16 py-12">
        <div className="mb-8 text-center">
          <img
            src="/images/wazin-logo.png"
            alt="Wazin Logo"
            className="h-16 md:h-20 w-auto object-contain mx-auto"
            onError={(e) => {
              e.target.src = '/images/wazin-icon.png';
            }}
          />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-8 mb-12">
          <div className="col-span-1">
            <h4 className="text-base font-bold mb-4 text-white border-b-2 border-wazin-gold pb-2">
              {i18n.language === 'ar' ? 'عن وازن' : 'About Wazin'}
            </h4>
            <ul className="space-y-2">
              {aboutLinks.map((link, index) => (
                <li key={index}>
                  <Link to={link.href} className="text-sm text-gray-300 hover:text-wazin-gold transition-colors flex items-center gap-2 group">
                    <span className="text-xs">•</span>
                    <span>{link.text}</span>
                    {link.external && <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="col-span-1">
            <h4 className="text-base font-bold mb-4 text-white border-b-2 border-wazin-gold pb-2">
              {i18n.language === 'ar' ? 'التواصل الإعلامي' : 'Media'}
            </h4>
            <ul className="space-y-2">
              {mediaLinks.map((link, index) => (
                <li key={index}>
                  <Link to={link.href} className="text-sm text-gray-300 hover:text-wazin-gold transition-colors flex items-center gap-2">
                    <span className="text-xs">•</span>
                    <span>{link.text}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="col-span-1">
            <h4 className="text-base font-bold mb-4 text-white border-b-2 border-wazin-gold pb-2">
              {i18n.language === 'ar' ? 'الدعم والمساعدة' : 'Support & Help'}
            </h4>
            <ul className="space-y-2">
              {supportLinks.map((link, index) => (
                <li key={index}>
                  <Link to={link.href} className="text-sm text-gray-300 hover:text-wazin-gold transition-colors flex items-center gap-2">
                    <span className="text-xs">•</span>
                    <span>{link.text}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="col-span-1">
            <h4 className="text-base font-bold mb-4 text-white border-b-2 border-wazin-gold pb-2">
              {i18n.language === 'ar' ? 'روابط مهمة' : 'Important Links'}
            </h4>
            <ul className="space-y-2">
              {importantLinks.map((link, index) => (
                <li key={index}>
                  <Link to={link.href} className="text-sm text-gray-300 hover:text-wazin-gold transition-colors flex items-center gap-2 group">
                    <span className="text-xs">•</span>
                    <span>{link.text}</span>
                    {link.external && <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="col-span-1">
            <h4 className="text-base font-bold mb-4 text-white border-b-2 border-wazin-gold pb-2">
              {i18n.language === 'ar' ? 'خدماتنا' : 'Our Services'}
            </h4>
            <ul className="space-y-2">
              {servicesLinks.map((link, index) => (
                <li key={index}>
                  <Link to={link.href} className="text-sm text-gray-300 hover:text-wazin-gold transition-colors flex items-center gap-2">
                    <span className="text-xs">•</span>
                    <span>{link.text}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="col-span-1">
            <h4 className="text-base font-bold mb-4 text-white border-b-2 border-wazin-gold pb-2">
              {i18n.language === 'ar' ? 'أدوات الإتاحة' : 'Accessibility'}
            </h4>
            <div className="flex gap-2">
              <button className="flex items-center justify-center p-3 border-2 border-white/30 hover:border-wazin-gold rounded-lg transition-all hover:bg-wazin-gold/10" aria-label="High Contrast">
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </button>
              <button className="flex items-center justify-center p-3 border-2 border-white/30 hover:border-wazin-gold rounded-lg transition-all hover:bg-wazin-gold/10" aria-label="Zoom Out">
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35M8 11h6" />
                </svg>
              </button>
              <button className="flex items-center justify-center p-3 border-2 border-white/30 hover:border-wazin-gold rounded-lg transition-all hover:bg-wazin-gold/10" aria-label="Zoom In">
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="M21 21l-4.35-4.35M11 8v6M8 11h6" />
                </svg>
              </button>
            </div>
          </div>

          <div className="col-span-1">
            <h4 className="text-base font-bold mb-4 text-white border-b-2 border-wazin-gold pb-2">
              {i18n.language === 'ar' ? 'وسائل التواصل الاجتماعي' : 'Social Media'}
            </h4>
            <div className="flex flex-wrap gap-2">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 border-2 border-white/30 hover:border-wazin-gold rounded flex items-center justify-center transition-all hover:bg-wazin-gold/10 group"
                  aria-label={social.label}
                  title={social.label}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>

            <div className="mt-6 pt-6 border-t border-white/20">
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm">
                  <Calendar className="w-5 h-5 text-white flex-shrink-0" />
                  <div>
                    <p className="text-gray-400 text-xs">{i18n.language === 'ar' ? 'أيام العمل' : 'Working Days'}</p>
                    <p className="text-white font-medium">{workingHours.days}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Clock className="w-5 h-5 text-white flex-shrink-0" />
                  <div>
                    <p className="text-gray-400 text-xs">{i18n.language === 'ar' ? 'ساعات العمل' : 'Working Hours'}</p>
                    <p className="text-white font-medium">{workingHours.hours}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="hidden">
          <div className="flex flex-wrap items-center justify-center gap-8 mb-4">
            <div className="flex items-center gap-3 text-center">
              <div className="flex flex-col">
                <span className="text-2xl font-bold text-wazin-gold">125,847</span>
                <span className="text-xs text-gray-400">{i18n.language === 'ar' ? 'عدد الزوار' : 'Visitors'}</span>
              </div>
            </div>
            <div className="flex items-center gap-3 text-center">
              <div className="flex flex-col">
                <span className="text-2xl font-bold text-wazin-gold">98,234</span>
                <span className="text-xs text-gray-400">{i18n.language === 'ar' ? 'الإعجابات' : 'Likes'}</span>
              </div>
            </div>
            <div className="flex items-center gap-3 text-center">
              <div className="flex flex-col">
                <span className="text-2xl font-bold text-wazin-gold">542,691</span>
                <span className="text-xs text-gray-400">{i18n.language === 'ar' ? 'المشاهدات' : 'Views'}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-white/20 pt-8 pb-6" dir={isArabic ? 'rtl' : 'ltr'}>
          <div className="text-center mb-7">
            <p className="relative text-lg md:text-xl tracking-[0.16em] text-transparent font-black mb-2">
              <span className="absolute inset-0 text-white">
                {isArabic ? wazinDataTitleAr : 'WAZIN DATA'}
              </span>
              {isArabic ? 'وازن بيانات' : 'WAZIN DATA'}
            </p>
            <h4 className="relative text-2xl md:text-3xl font-bold text-transparent mb-3">
              <span className="absolute inset-0 text-white">
                {isArabic ? wazinDataHeadlineAr : 'Operational Snapshot'}
              </span>
              {isArabic ? 'مؤشرات تشغيلية مختصرة' : 'Operational Snapshot'}
            </h4>
            <p className="relative text-transparent text-sm md:text-base">
              <span className="absolute inset-0 text-gray-300">
                {isArabic ? wazinDataDescriptionAr : 'Displayed data for the last 7 days.'}
              </span>
              {isArabic ? 'البيانات المعروضة لآخر 7 أيام.' : 'Displayed data for the last 7 days.'}
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {wazinData.map((item, index) => {
              const Icon = item.icon;
              const isGold = index % 2 === 0;

              return (
                <div
                  key={item.labelEn}
                  className={`rounded-2xl border px-4 py-5 text-center bg-white/5 transition-all duration-300 hover:-translate-y-1 ${
                    isGold ? 'border-wazin-gold/35 hover:border-wazin-gold' : 'border-white/15 hover:border-white/35'
                  }`}
                >
                  <div
                    className="w-11 h-11 mx-auto mb-4 rounded-full flex items-center justify-center bg-white/10 text-white"
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <p className="text-3xl md:text-4xl font-bold text-white mb-2 tabular-nums">
                    <CountUpNumber value={item.value} suffix={item.suffix} />
                  </p>
                  <p className="text-xs md:text-sm text-gray-300 font-medium leading-6">
                    {isArabic ? wazinDataLabelsAr[index] : item.labelEn}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        <div className="border-t border-white/20 pt-6">
          <div className="flex flex-col items-center justify-center gap-4 text-center">
            <div className="text-center">
              <p className="text-sm md:text-base text-gray-200 font-medium">
                {copyrightText}
              </p>
              <p className="hidden">
                © {currentYear} {i18n.language === 'ar' ? 'منصة وازن القانونية' : 'Wazin Legal Platform'} -{' '}
                {i18n.language === 'ar' ? 'جميع الحقوق محفوظة' : 'All Rights Reserved'}
              </p>
              <p className="hidden">
                {i18n.language === 'ar'
                  ? 'تم تطويره بمهنية واحترافية باستخدام أحدث التقنيات'
                  : 'Developed professionally using the latest technologies'}
              </p>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <Shield className="w-5 h-5 text-green-400" />
                <span>{i18n.language === 'ar' ? 'موقع آمن' : 'Secure Site'}</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <Lock className="w-5 h-5 text-green-400" />
                <span>SSL</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default FooterPro;
